import { Link } from "react-router-dom";
import { Badge } from "../components/ui/badge";
import { ArrowRight, Calendar, Clock, ArrowUpRight } from "lucide-react";

const posts = [
  {
    title: "Why Custom Software Beats Off-the-Shelf Solutions",
    excerpt: "When it comes to business software, one size rarely fits all. Discover why investing in custom software development can give your business a competitive edge that pre-built solutions simply can't match.",
    category: "Development",
    date: "Dec 15, 2025",
    readTime: "5 min read",
    slug: "#",
  },
  {
    title: "The Real Cost of Application Downtime",
    excerpt: "Application downtime costs businesses an average of $5,600 per minute. Learn how proactive hosting and maintenance can prevent costly outages and keep your revenue flowing.",
    category: "Hosting",
    date: "Dec 8, 2025",
    readTime: "4 min read",
    slug: "#",
  },
  {
    title: "How Vidyora is Transforming School Administration",
    excerpt: "A deep dive into how our school management system is helping educational institutions save time, reduce errors, and improve parent-school communication across Telangana.",
    category: "Product",
    date: "Nov 28, 2025",
    readTime: "6 min read",
    slug: "#",
  },
  {
    title: "React vs Next.js: Choosing the Right Framework",
    excerpt: "Both React and Next.js are powerful choices for modern web development. We break down when to use each and how to make the right decision for your project.",
    category: "Tech",
    date: "Nov 15, 2025",
    readTime: "7 min read",
    slug: "#",
  },
  {
    title: "5 Signs Your Application Needs a Performance Audit",
    excerpt: "Slow load times, high bounce rates, and frustrated users? These are signs your application might benefit from a comprehensive performance audit and optimization.",
    category: "Maintenance",
    date: "Nov 5, 2025",
    readTime: "4 min read",
    slug: "#",
  },
  {
    title: "Building Scalable APIs: Best Practices for 2025",
    excerpt: "From RESTful design to rate limiting and caching strategies, explore the best practices we follow at Scriptovative for building APIs that scale with your business.",
    category: "Development",
    date: "Oct 22, 2025",
    readTime: "8 min read",
    slug: "#",
  },
];

const categoryColors = {
  Development: "bg-indigo-500/10 text-indigo-400 border-indigo-500/20",
  Hosting: "bg-cyan-500/10 text-cyan-400 border-cyan-500/20",
  Product: "bg-orange-500/10 text-orange-400 border-orange-500/20",
  Tech: "bg-indigo-500/10 text-indigo-400 border-indigo-500/20",
  Maintenance: "bg-cyan-500/10 text-cyan-400 border-cyan-500/20",
};

export default function Blog() {
  return (
    <div data-testid="blog-page">
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28">
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="absolute top-1/3 right-1/4 w-80 h-80 bg-indigo-600/10 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-3xl">
            <Badge className="mb-4 bg-indigo-500/10 text-indigo-400 border-indigo-500/20 hover:bg-indigo-500/10 text-xs font-mono" data-testid="blog-badge">
              BLOG & INSIGHTS
            </Badge>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-50 leading-[1.1] tracking-tight">
              Thoughts on{" "}
              <span className="gradient-text">tech & innovation.</span>
            </h1>
            <p className="mt-6 text-base lg:text-lg text-slate-400 leading-relaxed max-w-xl">
              Insights, tutorials, and perspectives from our team on software 
              development, hosting, and the technology landscape.
            </p>
          </div>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="relative py-24 lg:py-32" data-testid="blog-posts">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Featured Post */}
          <div data-testid="featured-post" className="mb-12 p-8 lg:p-10 rounded-2xl bg-slate-900/50 border border-slate-800 hover:border-indigo-500/20 transition-all group">
            <div className="flex items-center gap-3 mb-4">
              <Badge className={`${categoryColors[posts[0].category]} hover:bg-transparent text-xs`}>
                {posts[0].category}
              </Badge>
              <span className="text-slate-500 text-xs flex items-center gap-1">
                <Calendar className="w-3 h-3" /> {posts[0].date}
              </span>
              <span className="text-slate-500 text-xs flex items-center gap-1">
                <Clock className="w-3 h-3" /> {posts[0].readTime}
              </span>
            </div>
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-50 mb-4 group-hover:text-indigo-400 transition-colors">
              {posts[0].title}
            </h2>
            <p className="text-slate-400 text-sm leading-relaxed max-w-2xl mb-6">{posts[0].excerpt}</p>
            <span className="inline-flex items-center gap-2 text-indigo-400 text-sm font-medium">
              Read article <ArrowUpRight className="w-4 h-4" />
            </span>
          </div>

          {/* Posts Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.slice(1).map((post, i) => (
              <article
                key={i}
                data-testid={`blog-post-${i}`}
                className="p-6 rounded-xl bg-slate-900/50 border border-slate-800 hover:border-indigo-500/20 transition-all group flex flex-col"
              >
                <div className="flex items-center gap-3 mb-4">
                  <Badge className={`${categoryColors[post.category]} hover:bg-transparent text-xs`}>
                    {post.category}
                  </Badge>
                  <span className="text-slate-500 text-xs">{post.readTime}</span>
                </div>
                <h3 className="font-['Outfit'] text-lg font-semibold text-slate-100 mb-3 group-hover:text-indigo-400 transition-colors flex-grow">
                  {post.title}
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed mb-4">{post.excerpt}</p>
                <div className="flex items-center justify-between mt-auto pt-4 border-t border-slate-800/50">
                  <span className="text-slate-500 text-xs flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> {post.date}
                  </span>
                  <span className="text-indigo-400 text-sm font-medium flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    Read <ArrowUpRight className="w-3 h-3" />
                  </span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="relative py-24 lg:py-32 bg-slate-900/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-slate-50 tracking-tight">
            Stay updated with our <span className="gradient-text">latest insights.</span>
          </h2>
          <p className="mt-4 text-base text-slate-400 max-w-lg mx-auto">
            We publish new articles regularly. Follow us for the latest in 
            software development and technology.
          </p>
          <Link
            to="/contact"
            data-testid="blog-cta"
            className="mt-8 inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-8 py-3.5 rounded-full font-medium transition-all shadow-[0_0_20px_rgba(99,102,241,0.3)] hover:shadow-[0_0_30px_rgba(99,102,241,0.5)] hover:-translate-y-0.5"
          >
            Get in Touch
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
